/**
 * Support API - Help & Feedback Requests
 *
 * POST /v1/support/requests    (any authed user, school-bound)
 *
 * Creates a Linear support request per submission — a Triage-state issue in the
 * SCH team linked to the school's Linear customer as a need (intake), tagged
 * with a type label and a Source-group label derived from the submitter's role
 * (parent / teacher). No platform-side persistence. The submitter's identity is
 * server-sourced from the request context; the contract deliberately carries NO
 * email field so a client can never impersonate another person.
 *
 * (Repointed from HubSpot to Linear in plan 2026-07-07-002 so app-submitted
 * requests land on the same board the school sees at /school/support.)
 */

import { z } from 'zod'

// ============================================================================
// POST /v1/support/requests
// ============================================================================

export const SupportRequestTypeSchema = z.enum(['bug_report', 'feature_request', 'question_feedback'])

export type SupportRequestType = z.infer<typeof SupportRequestTypeSchema>

export const MAX_SUPPORT_REQUEST_ATTACHMENTS = 3

/**
 * A pre-uploaded attachment reference. The client uploads media through the
 * platform's standard media pipeline BEFORE submitting and passes only the
 * hosted HTTPS URL here — the support endpoint never receives binaries.
 */
export const SupportRequestAttachmentSchema = z.object({
  url: z.string().trim().url().startsWith('https://').max(500),
  name: z.string().trim().min(1).max(120)
})

export type SupportRequestAttachment = z.infer<typeof SupportRequestAttachmentSchema>

export const CreateSupportRequestParamsSchema = z.object({
  type: SupportRequestTypeSchema,
  /**
   * Optional — when absent the backend derives the subject from the first
   * line of the message. Every subject (user-provided or derived) becomes the
   * Linear issue title; the school-slug prefix is added server-side.
   */
  subject: z.string().trim().min(1).max(200).optional(),
  message: z.string().trim().min(1).max(5000),
  /**
   * Optional pre-uploaded attachment references (max 3). Carried over into the
   * Linear issue description as a plain-text block server-side.
   */
  attachments: z.array(SupportRequestAttachmentSchema).max(MAX_SUPPORT_REQUEST_ATTACHMENTS).optional()
})

export type CreateSupportRequestParams = z.infer<typeof CreateSupportRequestParamsSchema>

export const CreateSupportRequestResponseSchema = z.object({
  submitted: z.literal(true),
  /** Human-readable identifier of the created Linear issue (e.g. "SCH-812"). */
  issueIdentifier: z.string()
})

export type CreateSupportRequestResponse = z.infer<typeof CreateSupportRequestResponseSchema>
